import { RequestBuilder } from '@angry-yard/coleslaw';
import { HttpClient } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Helpers } from '../../shared/helpers';

export class ODataRequest<T> extends RequestBuilder<T> {
  public type: { new(any?): T };

  constructor(private http: HttpClient, private url: string) {
    super();
  }

  private getParams(): string {
    let oDataSettings = this.createODataSettings();
    //oDataString will have skip/take/sort/filter/group
    //but not select
    if (this.oDataString && oDataSettings.$select) {
      this.oDataString += '&$select=' + oDataSettings.$select;
    }
    if (this.oDataString && oDataSettings.$count) {
      this.oDataString += '&$count=' + oDataSettings.$count;
    }
    if (this.oDataString && oDataSettings.$expand) {
      this.oDataString += '&$expand=' + oDataSettings.$expand;
    }

    let params = this.oDataString || this.createString(oDataSettings);

    return params;
  }

  public get(): Observable<Object> {
    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);

    return this.http
      .get(`${this.url}?${params}`);
  }

  public getTyped(): Observable<Array<T>> {
    if (!this.type) { throw new Error('Type must have a value'); }

    let params = this.getParams();

    return this.http
      .get(`${this.url}?${params}`).pipe(
        map((response: any) => Helpers.createClassArray(this.type, response.value)));
  }

  public getTypedResult(): Observable<GridDataResult> {
    if (!this.type) { throw new Error('Type must have a value') };

    let params = this.getParams();

    let result: Observable<GridDataResult> = this.http
      .get(`${this.url}?${params}`).pipe(
        map((response: any) => {
          let json = response;
          let value = Helpers.createClassArray(this.type, json.value);
          let res = { data: value, total: json['@odata.count'] };
          return res;
        }));
    return result;
  }

  public getResult(): Observable<GridDataResult> {
    let params = this.getParams();
    let result: Observable<GridDataResult> = this.http
      .get(`${this.url}?${params}`).pipe(
        map((response: any) => {
          let json = response;
          let res = { data: json.value, total: json['@odata.count'] }
          return res;
        }));
    return result;
  }

  private removeObjects(source: any): any {
    let data = Object.assign({}, source);
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        let m = data[key];
        if (typeof m === 'object' && !(m instanceof Date)) {
          delete data[key];
        }
      }
    }
    return data;
  }

  public post(data: object, removeObjects: boolean = true): Observable<Object> {
    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    let dataCopy;
    if (removeObjects) {
      dataCopy = this.removeObjects(data);
    } else {
      dataCopy = data;
    }
    return this.http.post(`${this.url}?${params}`, dataCopy);
  }

  public postTyped(data: T, removeObjects: boolean = true): Observable<Array<T>> {
    if (!this.type) { throw new Error('Type must have a value'); }

    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    let dataCopy: T;
    if (removeObjects) {
      dataCopy = this.removeObjects(data);
    } else {
      dataCopy = data;
    }

    return this.http
      .post(`${this.url}?${params}`, dataCopy).pipe(
        map((response: any) => Helpers.createClassArray(this.type, response.value)));
  }

  public put(id: number, data: object, removeObjects: boolean = true): Observable<Object> {
    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    let dataCopy;
    if (removeObjects) {
      dataCopy = this.removeObjects(data);
    } else {
      dataCopy = data;
    }

    return this.http.put(`${this.url}(${id})?${params}`, dataCopy);
  }

  public putTyped(id: number, data: T, removeObjects: boolean = true): Observable<Array<T>> {
    if (!this.type) { throw new Error('Type must have a value'); }

    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    let dataCopy;
    if (removeObjects) {
      dataCopy = this.removeObjects(data);
    } else {
      dataCopy = data;
    }
    return this.http
      .put(`${this.url}(${id})?${params}`, dataCopy).pipe(
        map((response: any) => Helpers.createClassArray(this.type, response.value)));
  }

  public postOrPatch(data: object, id?: number): Observable<Object> {
    if (id) {
      return this.patch(id, data);
    } else {
      return this.post(data);
    }
  }

  public patch(id: number, data: object, removeObjects: boolean = true): Observable<Object> {
    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    let dataCopy;
    if (removeObjects) {
      dataCopy = this.removeObjects(data);
    } else {
      dataCopy = data;
    }

    return this.http.patch(`${this.url}(${id})?${params}`, dataCopy);
  }

  public patchTyped(id: number, data: object, removeObjects: boolean = true): Observable<Array<T>> {
    if (!this.type) { throw new Error('Type must have a value'); }

    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    let dataCopy;
    if (removeObjects) {
      dataCopy = this.removeObjects(data);
    } else {
      dataCopy = data;
    }
    return this.http
      .patch(`${this.url}(${id})?${params}`, dataCopy).pipe(
        map((response: any) => Helpers.createClassArray(this.type, response.value)));
  }

  public delete(id: number, idField: string): Observable<Object> {
    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);
    params += `${idField}=${id}`;

    let x = this.http.delete(`${this.url}?${params}`);
    return x;
  }

  public deleteTyped(id: number, idField: string): Observable<Array<T>> {
    if (!this.type) { throw new Error('Type must have a value'); }

    let oDataSettings = this.createODataSettings();
    let params = this.oDataString || this.createString(oDataSettings);

    return this.http
      .delete(`${this.url}?${params}`).pipe(
        map((response: any) => Helpers.createClassArray(this.type, response.value)));
  }
}